import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';

import { VoiceboxApp } from 'bones/constants/voicebox/message';
import {
  VoiceboxCopy,
  VoiceboxSchema,
  VoiceboxTestIds,
} from 'bones/constants/voicebox/schema';
import { DEFAULT_VOICEBOX_SETTINGS } from 'bones/constants/voicebox/settings';
import { Icon } from 'bones/design-system/components/icons/Icon';
import { VoiceboxLogo } from 'bones/design-system/components/icons/VoiceboxLogo';
import { Heading } from 'bones/design-system/components/typography/Heading';
import { copyToClipboard, getIsClipboardEnabled } from 'bones/utils/clipboard';
import { isProduction } from 'bones/utils/environment';
import { noop } from 'bones/utils/noop';
import { getTestId, processTestIds } from 'bones/utils/processTestIds';

const defaultCopy: VoiceboxCopy = {
  voiceboxButton: {
    databaseDisabled: 'Voicebox is not enabled for this database.',
    noDatabase: 'Select a Voicebox-enabled database to chat with Voicebox.',
    text: 'Voicebox',
  },

  csvTable: {
    beforeLink: 'A preview of your results is shown above.',
    link: 'Download results',
    afterLink: 'for the full results set.',
  },

  drawer: {
    aiReasoning: 'Think Mode',
    empty: {
      title: 'Welcome to Stardog Voicebox!',
      subTitle: 'A simpler way to ask questions of your data.',
    },
    example: (idx: number) => `${idx + 1}`.padStart(2, '0'),
    placeholder: 'Ask Voicebox',
    submit: 'Send',
    title: (
      <Heading size="md" weight="light">
        <span key="VOICEB">VOICEB</span>
        <Icon key="LOGO" IconComponent={VoiceboxLogo} size="xl" />
        <span key="X">X</span>
      </Heading>
    ),
  },

  errors: {
    failure: () =>
      'Your request could not be processed. Please rephrase or ask a different question.',
    failureWithError: () =>
      'Your request could not be processed. Please rephrase or ask a different question.',
    unknownError: 'An unknown error occurred.',
  },

  feedback: {
    dialog: {
      cancelButton: 'Cancel',
      negativePlaceholder:
        '[Optional] What was the issue with the response? What could be improved?',
      submitButton: 'Provide feedback',
      title: 'Provide additional feedback',
    },
  },

  message: {
    description: {
      showLess: 'Less',
      showMore: 'More',
    },
    divider: {
      button: {
        initial: 'Settings',
        updated: 'Settings Updated',
      },
      content: {
        database: {
          label: 'Database:',
        },
        model: {
          default: 'Default',
          label: 'Model:',
        },
        namedGraphs: {
          label: 'Graph:',
        },
        reasoning: {
          label: 'Reasoning:',
          enabled: 'Yes',
          disabled: 'No',
        },
      },
    },
    document: {
      badge: 'Document Extracted',
    },
    example: {
      close: 'hide',
      help: 'Select a prompt from suggested',
      less: 'Less',
      more: 'More',
      title: 'Suggested prompts',
    },
    generated: {
      badge: 'Generated',
    },
    menu: {
      copyQuery: 'Copy query',
      copyResponse: 'Copy response',
      csvTable: {
        hide: 'Hide results table',
        show: 'Show results table',
      },
      chart: {
        chartError: 'Problem displaying chart, please try again',
        hide: 'Hide chart',
        show: 'Show chart',
      },
      downloadResults: 'Download results',
      externalLlm: 'This answer may contain hallucinations',
      llm: 'This answer may contain hallucinations',
      provideFeedback: 'Provide feedback',
      queryBuilder: {
        show: 'Explain response',
        hide: 'Hide explain response',
      },
      questionRewrittenAs: (query: string) =>
        `Voicebox interpreted your question as: ${query}`,
      regenerateResponse: 'Retry',
      safetyRag: 'This answer may contain hallucinations',
    },
    reasoning: {
      finished: 'Show reasoning steps',
      pending: 'Loading...',
    },
    noCatalogProviders: {
      beforeLink:
        'You do not have any data catalogs connected. To continue, please first',
      link: 'add data catalog(s)',
      afterLink: '.',
    },
    unverified: {
      badge: 'Unverified',
      button: 'Answer with general knowledge',
      message:
        'An answer to this question could not be found in your knowledge graph. Do you want the question answered using general knowledge?',
    },
    userSenderName: 'You',
  },

  panel: {
    header: {
      explorer: 'Open in Explorer',
      dataSources: 'Data sources',
    },
    analysis: {
      error: 'Problem analyzing data',
      hide: 'hide',
      placeholder: 'Ask for a trend or summary',
      retry: 'Retry',
      show: 'show',
      title: 'Analysis',
    },
    dataSources: {
      local: {
        graphs: 'Named Graph',
        types: 'Stardog Concepts',
      },
      virtual: {
        tables: 'Table Name',
        types: 'Stardog Concepts',
      },
    },
    attributes: {
      accordion: 'Details',
      label: 'Attribute',
      value: 'Value',
    },
    instances: {
      accordion: 'Instances',
      value: 'Instance',
    },
    messageSummary: {
      accordion: 'Summary Description',
      chart: {
        customize: 'Customize chart',
        error: 'Problem displaying chart',
        retry: 'Retry',
      },
      customizeChart: {
        cancel: 'Cancel',
        customizations: 'Changes',
        error: 'Problem displaying chart',
        retry: 'Retry',
        submit: 'Apply',
        title: 'Customize chart',
      },
      download: 'Download results',
      tabs: {
        chart: 'Chart',
        table: 'Table',
      },
    },
    queryExplanation: {
      accordion: 'Query Explanation',
      beta: 'BETA',
      copy: 'Copy query',
      queryBuilder: 'Query Visualization',
      sparql: 'SPARQL',
    },
    reasoning: {
      accordion: 'Reasoning Steps',
    },
    relationship: {
      accordion: 'Related Concepts',
      label: 'Relationship',
      value: 'Related Object',
    },
  },

  pending: {
    messages: [
      'Short pending message',
      'Really really really really really really really long pending message',
      'Another short pending message',
    ],
    timeout: 1,
  },

  queryBuilder: {
    incomplete: 'Some parts of the query are not shown above',
    seeQuery: 'see full query',
  },

  settingsAside: {
    database: {
      label: 'Database',
      placeholder: 'Select database',
    },
    endpoint: {
      label: 'Endpoint',
    },
    model: {
      label: 'Model',
      placeholder: 'Select model',
    },
    namedGraphs: {
      dividers: {
        alias: 'Aliases',
        named: 'Named',
        virtual: 'Virtual',
      },
      label: 'Graph',
      placeholder: 'Select graph',
      selected: (num: number) => `${num} selected`,
    },
    reasoning: {
      label: 'Reasoning',
    },
    resetButton: 'reset',
    submitButton: 'Save',
    title: 'Voicebox settings',
  },
};

export const UNPROCESSED_VOICEBOX_TEST_IDS: Omit<VoiceboxTestIds, ''> = {
  csvTable: {
    container: '',
    link: (iri, row, column) =>
      //  istanbul ignore next
      isProduction() ? `${row}-${column}` : iri,
  },

  drawer: {
    aiReasoning: '',
    body: '',
    input: '',
    loading: {
      conversationLoad: '',
      conversationRefetch: '',
    },
    submit: '',
  },

  feedback: {
    dialog: {
      body: '',
      cancelButton: '',
      input: '',
      loading: '',
      submitButton: '',
    },
  },

  messages: {
    avatar: {
      defaultUserAvatar: '',
      userInitials: '',
      voiceboxLogo: '',
    },
    chart: {
      container: '',
      error: '',
    },
    description: {
      loading: '',
      showLess: '',
      showMore: '',
      text: '',
    },
    divider: {
      button: '',
    },
    example: {
      badge: getTestId,
      button: getTestId,
      card: getTestId,
      close: '',
      less: '',
      more: '',
      open: '',
    },
    loading: '',
    menu: {
      button: '',
      copyQuery: '',
      copyResponse: '',
      downloadCsv: '',
      externalLlmMetadata: '',
      feedback: {
        loading: '',
        positive: '',
        negative: '',
      },
      llmMetadata: '',
      provideFeedback: '',
      queryBuilder: '',
      regenerateResponse: '',
      rewrittenQuestion: '',
      safetyRagMetadata: '',
      showChart: '',
      showCsvTable: '',
    },
    noCatalogProviders: {
      link: '',
      message: '',
    },
    reasoning: '',
    requestMessage: getTestId,
    responseExample: getTestId,
    responseDownloadCsv: getTestId,
    responseLink: getTestId,
    responseMessage: getTestId,
    responseQueryBuilderSeeQuery: getTestId,
    responseUnverified: getTestId,
  },

  panel: {
    breadcrumb: {
      item: getTestId,
      menu: '',
    },
    analysis: {
      hide: '',
      input: '',
      link: getTestId,
      loading: '',
      retry: '',
      send: '',
      show: '',
    },
    dataSources: {
      source: getTestId,
      type: (iri, rowIdx, typeIdx) =>
        //  istanbul ignore next
        isProduction() ? `${rowIdx}-${typeIdx}` : iri,
    },
    header: {
      explorer: '',
      dataSources: '',
      title: '',
      type: getTestId,
    },
    attributes: {
      accordion: '',
      link: getTestId,
      loadMore: getTestId,
    },
    instances: {
      accordion: '',
      link: getTestId,
      loadMore: '',
    },
    messageSummary: {
      accordion: '',
      chart: {
        chart: getTestId,
        container: '',
        customize: '',
        loading: '',
        retry: '',
      },
      customizeChart: {
        body: '',
        cancel: '',
        chart: getTestId,
        input: '',
        loading: '',
        remove: getTestId,
        retry: '',
        send: '',
        submit: '',
      },
      download: '',
      link: getTestId,
      tabs: {
        chart: '',
        table: '',
      },
    },
    queryExplanation: {
      accordion: '',
      copy: '',
      tabs: {
        queryBuilder: '',
        sparql: '',
      },
    },
    reasoning: {
      accordion: '',
    },
    relationships: {
      accordion: '',
      link: (iri, rowIdx, linkIdx) =>
        //  istanbul ignore next
        isProduction() ? `${rowIdx}-${linkIdx}` : iri,
      loadMore: getTestId,
    },
  },

  settingsAside: {
    database: {
      selector: '',
      selectorItem: getTestId,
    },
    endpoint: {
      selector: '',
    },
    model: {
      selector: '',
      selectorItem: getTestId,
    },
    namedGraphs: {
      filter: '',
      selector: '',
      selectorItem: getTestId,
    },
    reasoning: '',
    resetButton: '',
    submitButton: '',
  },

  voiceboxButton: '',
};

const defaultTestIds = cloneDeep(UNPROCESSED_VOICEBOX_TEST_IDS);
processTestIds(defaultTestIds, '-', 'sd-voicebox');

export const DEFAULT_VOICEBOX_SCHEMA: VoiceboxSchema = {
  copy: defaultCopy,
  testIds: { ...defaultTestIds },
  queryBuilder: null,

  clipboard: {
    isClipboardEnabled: getIsClipboardEnabled(),
    copyToClipboard,
  },

  config: {
    app: VoiceboxApp.CLOUD,
    catalogDatabaseId: 'catalog',
    connectionId: '',
    connectionIdx: 0,
    defaultSettings: DEFAULT_VOICEBOX_SETTINGS,
    initialAiReasoning: false,
    initialSettings: null,
  },

  drawer: {
    isOpen: false,
    toggleIsOpen: noop,
    usePortal: true,
  },

  links: {},

  settingsAside: {
    isOpen: false,
    onSaveSettings: noop,
    useSettings: false,
  },
};

export const VoiceboxSchemaContext = React.createContext<VoiceboxSchema>(
  DEFAULT_VOICEBOX_SCHEMA
);

export const useVoiceboxSchema = () => {
  const schema = React.useContext(VoiceboxSchemaContext);
  return schema;
};
